import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";
import type { CreateLeague } from "~pages/Leagues/types/upsertLeague.ts";
import type { League, LeagueResponse } from "~types/league.ts";

export const useCreateLeague = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const request = useApi();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation<League, Error, CreateLeague>({
    mutationFn: async (data) => {
      const response = await request<LeagueResponse>(`${sport}/o/${organizationId}/leagues`, {
        method: "POST",
        body: data,
      });

      return response?.data?.[0];
    },
    onSuccess: async (createdLeague) => {
      if (!createdLeague?.leagueId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        if (updatedScope.resources.leagues) {
          updatedScope.resources.leagues[createdLeague.leagueId] = createdLeague;
        } else {
          updatedScope.resources.leagues = {
            [createdLeague.leagueId]: createdLeague,
          };
        }

        updatedScope.scope.leagueTree.push({
          leagueId: createdLeague.leagueId,
          competitions: [],
        });
      });

      await Promise.all([invalidateCache(), queryClient.invalidateQueries({ queryKey: ["leagues", sport, organizationId] })]);
    },
  });
};
